/**
 * Session history synthesis — groups a flat UIMessage[] transcript into TURNS
 * for the history panel (Lovable-style revert / bookmark / undo surface).
 *
 * A TURN starts at each user message and runs until the next user message,
 * so it bundles the user's intent with everything the assistant produced in
 * response. Each turn carries a stable 0-based ordinal (`turnIndex` = the count
 * of user messages before it) — the SAME anchor the server stores bookmarks by
 * (sessions.bookmarked_turns) and the anchor "revert / branch from here" targets
 * via the user message id. Ordinals are durable because conversation history is
 * append-only (revert = branch, never destructive delete).
 *
 * Pure module: no React, no DOM. The tool-summary phrasing is delegated to
 * lib/timeline-synthesis (do NOT re-taxonomize tool names here) so the panel
 * reads with the same voice as the inline build-timeline.
 */

import { synthesizeToolSummary, type TimelineToolPart } from '@/lib/timeline-synthesis';
import { readToolPartInput } from '@/lib/tool-part-input';
import { parseTurnError } from '@/lib/turn-error';
import { stripSystemTime } from '@/lib/system-time-tag';

/** Minimal shape we need from a UIMessage — kept structural so tests need no SDK. */
export interface HistoryMessage {
  id: string;
  role: string;
  parts: Array<Record<string, unknown>>;
  metadata?: unknown;
}

export interface HistoryArtifactRef {
  /** `${messageId}-${partIndex}` — matches the artifact-candidate key in lab-chat-view. */
  key: string;
  toolName: string;
  /** Best-effort human label (input title/name → tool phrase). */
  label: string;
}

export interface HistoryTurn {
  /** Stable 0-based ordinal — bookmark anchor + revert target reference. */
  turnIndex: number;
  /** Current id of the user message that opens the turn (branch/revert atMessageId). */
  userMessageId: string;
  /** Short intent line for the row (truncated user text, or an attachment note). */
  title: string;
  /** Full user text — pre-filled into the branch composer on revert. */
  fullText: string;
  /** Epoch ms if derivable from the user message metadata, else undefined. */
  timestamp?: number;
  /** One-line rollup of what the assistant produced (may be ''). */
  summary: string;
  /** All message ids in this turn — used to match artifact candidates to the turn. */
  messageIds: string[];
  /** True if any assistant message in the turn carries a turn-failure marker. */
  hasError: boolean;
}

const TITLE_MAX = 90;

function textOf(message: HistoryMessage): string {
  const chunks: string[] = [];
  for (const part of message.parts) {
    if (part.type === 'text' && typeof part.text === 'string') {
      const t = stripSystemTime(part.text).trim();
      if (t) chunks.push(t);
    }
  }
  return chunks.join('\n\n').trim();
}

function attachmentNote(message: HistoryMessage): string | undefined {
  const files = message.parts.filter((p) => p.type === 'file').length;
  if (files > 0) return files === 1 ? '(attachment)' : `(${files} attachments)`;
  return undefined;
}

function truncate(s: string, max: number): string {
  const clean = s.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

function coerceTimestamp(metadata: unknown): number | undefined {
  const meta = metadata && typeof metadata === 'object' ? (metadata as Record<string, unknown>) : {};
  const raw = meta.createdAt ?? meta.timestamp ?? meta.created_at;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const parsed = Date.parse(raw);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return undefined;
}

function toolPartsOf(message: HistoryMessage): TimelineToolPart[] {
  const out: TimelineToolPart[] = [];
  for (const part of message.parts) {
    const type = typeof part.type === 'string' ? part.type : '';
    if (!type.startsWith('tool-')) continue;
    const state = part.state as string | undefined;
    if (state !== 'output-available' && state !== 'output-error') continue;
    out.push({
      toolName: type.replace('tool-', ''),
      // `output-error` parts have no `input` — the model's (often truncated)
      // arguments live in `rawInput`. See lib/tool-part-input.ts.
      input: readToolPartInput(part),
      output: part.output,
      errored: state === 'output-error',
    });
  }
  return out;
}

function messageHasError(message: HistoryMessage): boolean {
  for (const part of message.parts) {
    if (part.type === 'text' && typeof part.text === 'string' && parseTurnError(part.text)) return true;
  }
  const meta = message.metadata && typeof message.metadata === 'object'
    ? (message.metadata as Record<string, unknown>)
    : {};
  return meta.turnFailed === true;
}

/**
 * Group a transcript into turns, newest-relevant order preserved as source
 * order (caller reverses for "newest first" display). Leading non-user
 * messages (rare) are ignored — a turn is always opened by a user message.
 */
export function groupMessagesIntoTurns(messages: HistoryMessage[]): HistoryTurn[] {
  const turns: HistoryTurn[] = [];
  let userCount = 0;

  for (const message of messages) {
    if (message.role === 'user') {
      const fullText = textOf(message);
      const note = attachmentNote(message);
      const title = fullText
        ? truncate(fullText, TITLE_MAX)
        : note ?? 'New turn';
      turns.push({
        turnIndex: userCount,
        userMessageId: message.id,
        title,
        fullText,
        timestamp: coerceTimestamp(message.metadata),
        summary: '',
        messageIds: [message.id],
        hasError: false,
      });
      userCount += 1;
      continue;
    }

    // Non-user message (assistant/system/tool) — attach to the current turn.
    const turn = turns[turns.length - 1];
    if (!turn) continue; // leading non-user messages, no open turn yet
    turn.messageIds.push(message.id);
    if (messageHasError(message)) turn.hasError = true;
  }

  // Second pass: build each turn's tool summary from its assistant messages.
  // Done after grouping so a turn spanning multiple assistant messages rolls up
  // as one line.
  const byId = new Map(messages.map((m) => [m.id, m] as const));
  for (const turn of turns) {
    const parts: TimelineToolPart[] = [];
    for (const mid of turn.messageIds) {
      const m = byId.get(mid);
      if (!m || m.role === 'user') continue;
      parts.push(...toolPartsOf(m));
    }
    turn.summary = parts.length > 0 ? synthesizeToolSummary(parts) : '';
  }

  return turns;
}
